/**
 * Amazon affiliate URL builder — geographic routing (OneLink-style fallback)
 *
 * Detects the visitor's country and rewrites Amazon product URLs so the
 * domain (.com, .es, .co.uk, ...) and the affiliate tag both match that
 * country. This mirrors what Amazon's OneLink JS does automatically.
 *
 * Country is detected with three strategies, in order:
 *   1. Cached country code (sessionStorage)
 *   2. Free public geo-IP (ipapi.co) on first call
 *   3. Browser locale fallback (navigator.language)
 *
 * Result is asynchronous, so the consumer waits on a Promise.
 */

const COUNTRY_CACHE_KEY = 'llql_country_v1';

// Country code -> Amazon marketplace + affiliate tag
const COUNTRY_MAP = {
  US: { domain: 'www.amazon.com',     tag: 'lalunaqueladr-20'  },
  CA: { domain: 'www.amazon.ca',      tag: 'lalunaquela0e-20'  },
  FR: { domain: 'www.amazon.fr',      tag: 'lalunaquela0c-21'  },
  DE: { domain: 'www.amazon.de',      tag: 'lalunaquel0fd-21'  },
  IT: { domain: 'www.amazon.it',      tag: 'lalunaquela0a-21'  },
  NL: { domain: 'www.amazon.nl',      tag: 'lalunaquela00-21'  },
  ES: { domain: 'www.amazon.es',      tag: 'lalunaqueladr-21'  },
  GB: { domain: 'www.amazon.co.uk',   tag: 'lalunaquela0d-21'  },
  UK: { domain: 'www.amazon.co.uk',   tag: 'lalunaquela0d-21'  }
};

const DEFAULT = COUNTRY_MAP.US;

// Best-effort mapping browser locale -> country code
function localeToCountry() {
  if (typeof navigator === 'undefined') return null;
  const langs = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language || ''];

  for (const lang of langs) {
    if (!lang) continue;
    const parts = lang.split('-');
    if (parts.length >= 2) {
      const cc = parts[1].toUpperCase();
      if (COUNTRY_MAP[cc]) return cc;
    }
    // Common language-only fallbacks
    const baseMap = {
      en: null,        // ambiguous, leave for geo-IP
      es: 'ES',
      fr: 'FR',
      de: 'DE',
      it: 'IT',
      nl: 'NL'
    };
    const code = baseMap[parts[0].toLowerCase()];
    if (code) return code;
  }
  return null;
}

let _countryPromise = null;

/**
 * Returns the visitor's ISO country code (uppercase, e.g. "ES", "US").
 * Cached in sessionStorage to avoid repeated geo-IP calls.
 */
export function detectCountry() {
  if (_countryPromise) return _countryPromise;

  _countryPromise = (async () => {
    try {
      const cached = typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(COUNTRY_CACHE_KEY)
        : null;
      if (cached) return cached.toUpperCase();
    } catch (_) {}

    // Try geo-IP (free, no API key needed). 1.5s timeout so we never block UX.
    try {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 1500);
      const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
      clearTimeout(t);
      if (res.ok) {
        const data = await res.json();
        if (data && data.country_code) {
          const cc = String(data.country_code).toUpperCase();
          try { sessionStorage.setItem(COUNTRY_CACHE_KEY, cc); } catch (_) {}
          return cc;
        }
      }
    } catch (_) {}

    // Fallback to browser locale
    const locale = localeToCountry();
    const cc = locale || 'US';
    try { sessionStorage.setItem(COUNTRY_CACHE_KEY, cc); } catch (_) {}
    return cc;
  })();

  return _countryPromise;
}

/**
 * Build a localized Amazon URL for a given ASIN.
 * @param {string} asin - Amazon Standard Identification Number, e.g. "B0GX2XB5HV"
 * @param {string} countryCode - ISO country code (uppercase)
 * @returns {string} A complete Amazon product URL with the correct tag.
 */
export function buildAmazonUrl(asin, countryCode) {
  const cfg = COUNTRY_MAP[(countryCode || '').toUpperCase()] || DEFAULT;
  return `https://${cfg.domain}/dp/${asin}/?tag=${cfg.tag}`;
}

/**
 * Extract the ASIN from any Amazon product URL.
 * Returns null if not found.
 */
export function extractAsin(url) {
  if (!url) return null;
  const m = url.match(/\/dp\/([A-Z0-9]{10})/i);
  return m ? m[1].toUpperCase() : null;
}

/**
 * Convenience helper: given any Amazon URL (with our tag), returns the
 * geographically-localized variant. Falls back to the original URL if it
 * cannot be parsed.
 */
export async function localizeAmazonUrl(originalUrl) {
  const asin = extractAsin(originalUrl);
  if (!asin) return originalUrl;
  const country = await detectCountry();
  return buildAmazonUrl(asin, country);
}
